// MUNYA OS · #150 · Campanita de notificaciones en la barra superior
//
// Muestra lo que requiere atención: flujos de aprobación pendientes
// (ausencias, correcciones de fichaje), resoluciones de sugerencias del
// track, SLA, firmas… Cada usuario ve SOLO sus notificaciones (el API
// filtra por user_id). Polling suave cada 60 s.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
} from 'react';
import { Archive, Bell, Check } from '@phosphor-icons/react';
import { api } from '../lib/api.js';
import { navigate } from '../lib/router.js';

interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  cta_url: string | null;
  cta_label: string | null;
  related_kind: string | null;
  related_id: string | null;
  severity: string;
  read_at: string | null;
  created_at: string;
}

// Destino de la notificación: usa cta_url si lo trae; si no, lo deriva de
// related_kind (muchas notificaciones se crearon sin cta_url y no llevaban a
// ningún sitio — track_item, app_feedback, expense, selection_item).
function notificationTarget(n: Notification): string | null {
  if (n.cta_url) return n.cta_url;
  switch (n.related_kind) {
    case 'track_item':
      return n.related_id ? `/track?item=${n.related_id}` : '/track';
    case 'app_feedback':
      return '/administracion?tab=feedback';
    case 'expense':
    case 'studio_expense':
      return '/contabilidad';
    case 'selection_item':
      return '/proyectos';
    case 'portal_client_reply':
      return '/proyectos';
    case 'onedrive_drift':
      return '/administracion?tab=backups';
    default:
      return null;
  }
}

function timeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

export function NotificationsBell(): ReactElement {
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.notificationsList();
      setItems(r.items);
      setUnread(r.unread);
    } catch {
      /* sesión caducada o red: la campanita no rompe la app */
    }
  }, []);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => {
      void load();
    }, 60_000);
    return () => {
      window.clearInterval(t);
    };
  }, [load]);

  // Cerrar al clicar fuera
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const onItemClick = async (n: Notification): Promise<void> => {
    if (!n.read_at) {
      setItems((arr) => arr.map((i) => (i.id === n.id ? { ...i, read_at: 'now' } : i)));
      setUnread((u) => Math.max(0, u - 1));
      api.notificationRead(n.id).catch(() => undefined);
    }
    const target = notificationTarget(n);
    if (target) {
      setOpen(false);
      navigate(target);
    }
  };

  const readAll = async (): Promise<void> => {
    setItems((arr) => arr.map((i) => ({ ...i, read_at: i.read_at ?? 'now' })));
    setUnread(0);
    api.notificationsReadAll().catch(() => undefined);
  };

  // #358 · archivar una notificación: la saca del listado (el API filtra archived_at IS NULL).
  const archive = (e: ReactMouseEvent, n: Notification): void => {
    e.stopPropagation();
    setItems((arr) => arr.filter((i) => i.id !== n.id));
    if (!n.read_at) setUnread((u) => Math.max(0, u - 1));
    api.notificationArchive(n.id).catch(() => undefined);
  };

  const archiveAll = (): void => {
    setItems([]);
    setUnread(0);
    api.notificationsArchiveAll().catch(() => undefined);
  };

  return (
    <div ref={rootRef} className="relative" data-focus-hide>
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        className="mn-nav-inactive relative inline-flex items-center h-[2.25rem] w-[2.25rem] justify-center lg:h-5 lg:w-auto lg:px-1.5 rounded text-xs leading-none transition"
        title="Notificaciones"
        aria-label={`Notificaciones${unread > 0 ? ` · ${unread} sin leer` : ''}`}
      >
        <Bell size={13} weight={unread > 0 ? 'fill' : 'regular'} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-[var(--color-munya-olive)] text-white text-[9px] leading-[14px] text-center font-medium">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-7 z-50 w-80 max-lg:w-[20rem] max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto rounded-lg border border-[var(--color-munya-sand)] bg-[var(--color-munya-cream)] shadow-2xl">
          <header className="sticky top-0 bg-[var(--color-munya-cream)] border-b border-[var(--color-munya-sand)] px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold">Notificaciones</span>
            <span className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => void readAll()}
                  className="text-[10px] inline-flex items-center gap-1 opacity-60 hover:opacity-100"
                >
                  <Check size={10} weight="bold" /> Marcar leídas
                </button>
              )}
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={archiveAll}
                  className="text-[10px] inline-flex items-center gap-1 opacity-60 hover:opacity-100"
                >
                  <Archive size={10} weight="bold" /> Archivar todas
                </button>
              )}
            </span>
          </header>
          {items.length === 0 ? (
            <p className="text-xs opacity-50 italic text-center py-8">Sin notificaciones.</p>
          ) : (
            <ul>
              {items.slice(0, 40).map((n) => (
                <li
                  key={n.id}
                  className="border-b border-[var(--color-munya-sand)]/40 flex items-stretch"
                >
                  <button
                    type="button"
                    onClick={() => void onItemClick(n)}
                    className={`flex-1 min-w-0 text-left px-3 py-2 hover:bg-[var(--color-munya-sand)]/20 transition ${n.read_at ? 'opacity-55' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                          n.read_at
                            ? 'bg-transparent'
                            : n.severity === 'high'
                              ? 'bg-[var(--color-munya-destructive)]'
                              : 'bg-[var(--color-munya-olive)]'
                        }`}
                      />
                      <span className="min-w-0">
                        <span className="block text-xs font-medium leading-snug">{n.title}</span>
                        {n.body && (
                          <span className="block text-[11px] opacity-70 leading-snug line-clamp-2">
                            {n.body}
                          </span>
                        )}
                        <span className="block text-[10px] opacity-40 mt-0.5">
                          {timeAgo(n.created_at)}
                        </span>
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      archive(e, n);
                    }}
                    className="shrink-0 px-2.5 flex items-center opacity-30 hover:opacity-100 hover:bg-[var(--color-munya-sand)]/20 transition"
                    title="Archivar"
                    aria-label="Archivar notificación"
                  >
                    <Archive size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
